/* eslint-disable formatjs/no-literal-string-in-jsx --
 * Legal docs untranslated */
import type { ReactElement } from 'react';
import { ODSContainer } from 'bc-ods';

function Privacy(): ReactElement {
  return (
    <div className="h-100 w-100 overflow-y-auto">
      <ODSContainer
        data-testid="content-privacy-statement"
        className="mt-5"
        style={{ maxWidth: '100ch' }}
      >
        <h1>Privacy Policy</h1>
        <p className="small">Last updated May 14, 2019</p>
        <h2>What information Onebrief collects and why</h2>
        <h3>Information from website browsers</h3>
        <p>
          If you are <strong>just browsing the website</strong>, we collect the same basic
          information that most websites collect. We use common internet technologies, such as
          cookies and web server logs.
        </p>
        <p>
          The information we collect about all visitors to our website includes the visitor’s
          browser type, language preference, referring site, additional websites requested, and the
          date and time of each visitor request. We also collect potentially personally-identifying
          information like Internet Protocol (IP) addresses.
        </p>
        <h5>Why we collect this information</h5>
        <p>
          We collect this information to better understand how our website visitors use Onebrief,
          and to monitor and protect the security of the website.
        </p>
        <h3>Information from users with accounts</h3>
        <p>
          If you sign up for the beta test, we require some basic information. We will ask you for a
          valid email address. We may ask you to create a username and password, or we may assign
          one to you. You also have the option to give us more information if you want to, and this
          may include &quot;User Personal Information.&quot;
        </p>
        <p>
          &quot;User Personal Information&quot; is any information about one of our users which
          could, alone or together with other information, personally identify him or her.
          Information such as a user name and password, an email address, a real name, and a
          photograph are examples of “User Personal Information.” User Personal Information includes
          Personal Data as defined in the General Data Protection Regulation.
        </p>
        <p>
          We may use aggregated, non-personally identifying information to operate, improve, and
          optimize our website and service.
        </p>
        <h5>Why we collect this information</h5>
        <p>
          We need your User Personal Information to create your account, and to provide the services
          you request, including to provide the Onebrief service or to respond to support requests.
        </p>
        <p>
          We use your User Personal Information, specifically your user name or email address, to
          identify you on Onebrief.
        </p>
        <p>
          We will use your email address to communicate with you. Please see our section on email
          communication for more information.
        </p>
        <p>
          We use your User Personal Information for internal purposes, such as to maintain logs for
          security reasons, for training purposes, and for legal documentation.
        </p>
        <p>
          We limit our use of your User Personal Information to the purposes listed in this Privacy
          Statement. If we need to use your User Personal Information for other purposes, we will
          ask your permission first.
        </p>
        <h2>Our legal basis for processing information</h2>
        <p>
          Under certain international laws (including GDPR), Onebrief is required to notify you
          about the legal basis on which we process User Personal Information. Onebrief processes
          User Personal Information on the following legal bases:
        </p>
        <p>
          When you create a Onebrief account, you provide an email address and possibly other
          personal information. We require those data elements for you to enter into this Agreement
          with us, and we process those elements on the basis of performing that contract.
        </p>
        <p>
          Generally, the remainder of the processing of personal information we perform is necessary
          for the purposes of our legitimate interests. For example, for security purposes, we must
          keep logs of IP addresses that access Onebrief, and in order to respond to legal process,
          we are required to keep records of users who have sent and received DMCA takedown notices.
        </p>
        <p>
          If you would like to request erasure of data we process on the basis of consent or object
          to our processing of personal information, please email us at{' '}
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>{' '}
          .
        </p>
        <h2>What information Onebrief does not collect</h2>
        <p>
          We do not intentionally collect <strong>sensitive personal information</strong>, such as
          social security numbers, genetic data, health information, or religious information.
          Although Onebrief does not request or intentionally collect any sensitive personal
          information, we realize that you might store this kind of information in your account. If
          you store any sensitive personal information on our servers, you are responsible for
          complying with any regulatory controls regarding that data.
        </p>
        <p>
          If you are a <strong>child under the age of 13</strong>, you may not have an account on
          Onebrief. Onebrief does not knowingly collect information from or direct any of our
          content specifically to children under 13. If we learn or have reason to suspect that you
          are a user who is under the age of 13, we will unfortunately have to close your account.
          Other countries may have different minimum age limits, and if you are below the minimum
          age for providing consent for data collection in your country, you may not use Onebrief
          without obtaining your parents&apos; or legal guardians&apos; consent.
        </p>
        <h2>How we share the information we collect</h2>
        <p>
          We <strong>do not</strong> share, sell, rent, or trade User Personal Information with
          third parties for their commercial purposes.
        </p>
        <p>
          We <strong>do not</strong> disclose User Personal Information outside Onebrief, except in
          the situations listed in this section or in the section below on Compelled Disclosure.
        </p>
        <p>
          We <strong>do</strong> share certain aggregated, non-personally identifying information
          with others about how our users, collectively, use Onebrief, or how our users respond to
          our other offerings, such as conferences or events.
        </p>
        <p>
          We <strong>do</strong> share aggregated, non-personally identifying information with third
          parties.
        </p>
        <p>
          We <strong>may</strong> share User Personal Information if we are involved in a merger,
          sale, or acquisition. If any such change of ownership happens, we will ensure that it is
          under terms that preserve the confidentiality of User Personal Information, and we will
          notify you on our website or by email before any transfer of your User Personal
          Information. The organization receiving any User Personal Information will have to honor
          any promises we have made in this Agreement.
        </p>
        <h2>How you can access and control the information we collect</h2>
        <p>
          As this is very early beta software, you won’t necessarily be able to access, update,
          alter, or delete your information within the software. You can access, update, alter, or
          delete your information by emailing{' '}
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
          .
        </p>
        <h2>Data retention and deletion of data</h2>
        <p>
          Generally, Onebrief will retain User Personal Information for as long as your account is
          active or as needed to provide you services.
        </p>
        <p>
          We may retain certain User Personal Information indefinitely, unless you delete it or
          request its deletion. For example, we don’t automatically delete inactive user accounts,
          so unless you choose to delete your account, we will retain your account information
          indefinitely.
        </p>
        <p>
          If you would like to cancel your account or delete your User Personal Information, you may
          do so by emailing{' '}
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
          . We will retain and use your information as necessary to comply with our legal
          obligations, resolve disputes, and enforce our agreements, but barring legal requirements,
          we will delete your full profile (within reason) within 90 days.
        </p>
        <p>
          After an account has been deleted, certain data, such as your contributions and comments
          to plan on Onebrief, will remain. However, we will delete or deidentify your personal
          information, including your user name and email address.
        </p>
        <h2>Our use of cookies and tracking</h2>
        <h3>Cookies</h3>
        <p>
          Onebrief uses cookies to make interactions with our service easy and meaningful. We use
          cookies (and similar technologies) to keep you logged in, remember your preferences, and
          provide information for future development of Onebrief. We also use cookies to identify a
          device, for security reasons. By using our website, you agree that we can place these
          types of cookies on your computer or device. If you disable your browser or device’s
          ability to accept cookies, you may not be able to log in or use Onebrief’s services.
        </p>
        <h2>How we respond to compelled disclosure</h2>
        <p>
          Onebrief may disclose personally-identifying information or other information we collect
          about you to law enforcement in response to a valid subpoena, court order, warrant, or
          similar government order, or when we believe in good faith that disclosure is reasonably
          necessary to protect our property or rights, or those of third parties or the public at
          large.
        </p>
        <p>
          In complying with court orders and similar legal processes, Onebrief strives for
          transparency. When permitted, we will make a reasonable effort to notify users of any
          disclosure of their information, unless we are prohibited by law or court order from doing
          so, or in rare, exigent circumstances.
        </p>
        <h2>How we communicate with you</h2>
        <p>
          We will use your email address to communicate with you for notifications, support, and
          marketing. You may unsubscribe to marketing emails by replying directly to the email or
          contacting{' '}
          <a href="&#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
          .
        </p>
        <p>
          Our emails might contain a pixel tag, which is a small, clear image that can tell us
          whether or not you have opened an email and what your IP address is. We use this pixel tag
          to make our email more effective for you and to make sure we’re not sending you unwanted
          email.
        </p>
        <h2>Resolving complaints</h2>
        <p>
          If you have concerns about the way Onebrief is handling your User Personal Information,
          please let us know immediately. We want to help. You may contact us at{' '}
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#112;&#114;&#105;&#118;&#97;&#99;&#121;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
          .
        </p>
      </ODSContainer>
    </div>
  );
}

export default Privacy;
